import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useLocation } from "react-router";
import { useLocalStorage } from "react-use";
import { ControlledMenu, MenuItem, useMenuState } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/zoom.css";
import {
    CircleStackIcon,
    DocumentTextIcon,
    HomeIcon,
    MegaphoneIcon,
    NewspaperIcon,
    WrenchScrewdriverIcon,
    ChartBarIcon,
    ArchiveBoxIcon,
    PhoneIcon,
    CpuChipIcon,
} from "@heroicons/react/24/solid";
import CampaignIcon from "@mui/icons-material/Campaign";
import { useStore } from "../../helpers";

const navigation = [
    { name: "DashBoard", href: "/", icon: HomeIcon },
    { name: "Data Bank", href: "/databank", icon: CircleStackIcon },
    { name: "Reports", href: "/pbidashboard", icon: DocumentTextIcon },
    { name: "Social Listening", href: "/social_listening", icon: MegaphoneIcon },
    { name: "TARS", href: "/tars", icon: ArchiveBoxIcon },
    {
        name: "Cockpit",
        href: "/cockpit",
        icon: ChartBarIcon,
        isVisible: (userStore: any) => userStore.isRoot(),
    },
    {
        name: "LLM Usage",
        href: "/llm-usage",
        icon: CpuChipIcon,
        isVisible: (userStore: any) => userStore.isRoot(),
    },
    {
        name: "Reach Out Campaigns",
        href: "/campaigns",
        icon: CampaignIcon,
        isVisible: (userStore: any) => userStore.isRoot(),
    },
    { name: "Media Reports", href: "/media", icon: NewspaperIcon },
    { name: "Nova Voice", href: "/nova", icon: PhoneIcon },
    { name: "Settings", href: "/settings", icon: WrenchScrewdriverIcon },
];

const NavItemsDropdown = observer(() => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [value, setValue] = useLocalStorage("active_route", "/");
    const [activeRoute, setActiveRoute] = useState<string>(value || "/");
    const logoRef = useRef<HTMLDivElement | null>(null);
    const [menuState, toggleMenu] = useMenuState({ transition: true });
    const { userStore } = useStore();

    useEffect(() => {
        setActiveRoute(value || "/");
    }, [pathname, value]);

    const handleRouteSelect = (href: string) => {
        navigate(href);
        setValue(href);
        setActiveRoute(href);
        toggleMenu(false);
    };

    return (
        <>
            <div
                ref={logoRef}
                onClick={() => toggleMenu(true)}
                role="button"
                aria-label="Open navigation"
                className="z-[999] flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-md bg-white shadow-sm transition-all sm:h-11 sm:w-11 md:h-12 md:w-12"
                style={{ display: "inline-flex" }}
            >
                <img
                    src="/images/srimages/dashboardnewlogo.svg"
                    alt="logo"
                    className="block h-7 w-7 object-contain sm:h-8 sm:w-8"
                    style={{ display: "block" }}
                />
            </div>

            <ControlledMenu
                {...menuState}
                anchorRef={logoRef}
                onClose={() => toggleMenu(false)}
                direction="bottom"
                position="anchor"
                arrow
                transition
                portal
                menuStyle={{
                    borderRadius: "14px",
                    boxShadow: "0px 6px 18px rgba(0, 0, 0, 0.15)",
                    border: "1px solid #E5E7EB",
                    padding: "6px",
                    backgroundColor: "#fff",
                    minWidth: "190px",
                }}
            >
                {navigation.map((item) => {
                    if (item.isVisible && !item.isVisible(userStore)) return null;
                    const isMediaRoute =
                        pathname.startsWith("/print_media_reports") ||
                        pathname === "/media" ||
                        pathname.startsWith("/manage_search_terms");
                    const isActive =
                        activeRoute === item.href || (item.name === "Media Reports" && isMediaRoute);
                    return (
                        <MenuItem
                            key={item.href}
                            onClick={() => handleRouteSelect(item.href)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "10px 14px",
                                borderRadius: "10px",
                                margin: "3px 6px",
                                fontSize: "15px",
                                fontWeight: isActive ? 600 : 500,
                                backgroundColor: isActive ? "#000" : "transparent",
                                color: isActive ? "#fff" : "#000",
                                transition: "all 0.25s ease",
                            }}
                            onMouseEnter={(e) => {
                                if (!isActive) e.currentTarget.style.backgroundColor = "#F3F4F6";
                            }}
                            onMouseLeave={(e) => {
                                if (!isActive) e.currentTarget.style.backgroundColor = "transparent";
                            }}
                        >
                            <item.icon className="h-5 w-5" style={{ color: isActive ? "#fff" : "#000" }} />
                            <span>{item.name}</span>
                        </MenuItem>
                    );
                })}
            </ControlledMenu>
        </>
    );
});

export default NavItemsDropdown;
